import { useState, useEffect } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/_db";
import { useAuth } from "@/contexts/AuthContext";
import { Company } from "@/types/company/company.types";

export const useCurrentUserCompany = () => {
    const { user, claims } = useAuth();
    const [company, setCompany] = useState<Company | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!user?.uid || !claims?.companyId) {
            setCompany(null);
            setLoading(false);
            return;
        }

        let unsubscribe: (() => void) | undefined;

        const fetchCompany = async () => {
            try {
                setLoading(true);
                setError(null);

                // Ensure companyId is defined (TypeScript type guard)
                if (!claims.companyId) {
                    setCompany(null);
                    setLoading(false);
                    return;
                }

                // Get the company document directly using the companyId from claims
                const companyDocRef = doc(db, "companies", claims.companyId);

                // Set up real-time listener
                unsubscribe = onSnapshot(
                    companyDocRef,
                    (docSnapshot) => {
                        if (docSnapshot.exists()) {
                            const companyData = {
                                id: docSnapshot.id,
                                ...docSnapshot.data(),
                            } as Company;
                            setCompany(companyData);
                        } else {
                            setCompany(null);
                        }
                        setLoading(false);
                    },
                    (err) => {
                        console.error("Error fetching company document:", err);
                        setError(err);
                        setLoading(false);
                    }
                );
            } catch (err) {
                console.error("Error setting up company listener:", err);
                setError(err as Error);
                setLoading(false);
            }
        };

        fetchCompany();

        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [user?.uid, claims?.companyId]);

    return { company, loading, error };
}; 